import { Alert, Anchor, Group, Stack, Table, Text, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconMinus,
  IconSelector,
  IconStarOff,
} from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DateRangePicker from '../../../components/DateRangePicker/DateRangePicker';
import ErrorMessage from '../../../components/ErrorMessage';
import Spinner from '../../../components/Spinner';
import prettifiedLabel from '../../../constants/labelPrettifier';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../../helpers/dateUtils';
import getErrorMessage from '../../../helpers/getErrorMessage';
import { EM_DASH } from '../../../helpers/rfmAnalysis';
import {
  activeChannelKeys,
  channelSortKey,
  describeSizeHistory,
  formatConversionValue,
  formatCount,
  formatSizeChange,
  sizeChangeTone,
  sortSegmentRows,
} from '../../../helpers/segmentPerformance';
import useFetch from '../../../helpers/useFetch';

const INITIAL_PERIOD_DAYS = 30;

// The dates go out as full ISO datetimes at the local day boundaries, the way
// every other analytics route on this domain is called. This endpoint treats the
// range as real timestamps (half-open: > startDate, <= endDate), so local
// midnight is exactly the boundary the user asked for.
//
// This is deliberately the opposite of the sibling RfmAnalysisTab, which must
// send bare YYYY-MM-DD because that endpoint reduces its params to day labels.
// Do not harmonise the two - one of them would start answering for the wrong day.
const segmentPerformanceDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, startTs, endTs) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/segmentPerformance/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`
    ),
  300
);

const toneStyles = {
  positive: { color: 'var(--mantine-color-success-5)', Icon: IconArrowUp },
  negative: { color: 'var(--mantine-color-danger-5)', Icon: IconArrowDown },
  neutral: { color: 'var(--mantine-color-dimmed)', Icon: IconMinus },
};

// 'recommender' is the API's name for what the UI calls a Product Block everywhere else.
const channelLabel = (channel) => (channel === 'recommender' ? 'Product Block' : prettifiedLabel(channel));

// A name column reads naturally A-Z first; a metric column is interesting from
// the top down.
const defaultDirectionFor = (sortKey) => (sortKey === 'name' ? 'asc' : 'desc');

const SortableTh = ({ sortKey, label, sort, onSort }) => {
  const active = sort.by === sortKey;
  const Icon = active ? (sort.direction === 'asc' ? IconChevronUp : IconChevronDown) : IconSelector;

  return (
    <Table.Th aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <UnstyledButton onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`}>
        <Group gap={4} wrap="nowrap">
          <Text size="sm" fw={600}>
            {label}
          </Text>
          <Icon size={14} style={{ color: active ? undefined : 'var(--mantine-color-dimmed)' }} />
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
};

// null conversions mean the segment has never been calculated - it is not in use
// by a running campaign or web personalization, so nothing attributes sales to
// it. Rendering that as 0 would claim it sells nothing.
const NotTrackedCell = () => (
  <Tooltip
    label="Nothing measures this segment yet. Attach it to a running campaign or a web personalization for conversions to be attributed to it."
    multiline
    w={280}
    withArrow
  >
    <Text size="sm" c="dimmed" style={{ cursor: 'help' }}>
      Not tracked
    </Text>
  </Tooltip>
);

const SizeChangeCell = ({ sizeChange, sizeAtStart }) => {
  const tone = sizeChangeTone(sizeChange, sizeAtStart);
  const toneStyle = tone ? toneStyles[tone] : null;

  return (
    <Group gap={4} wrap="nowrap">
      {toneStyle && <toneStyle.Icon size={16} style={{ color: toneStyle.color }} />}
      <Text size="sm" c={toneStyle ? toneStyle.color : undefined}>
        {formatSizeChange(sizeChange)}
      </Text>
    </Group>
  );
};

const AudiencePerformanceTab = () => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const currency = useSelector((state) => state.domain.domain.currency);

  const [startDate, setStartDate] = useState(daysAgoISODate(INITIAL_PERIOD_DAYS - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [performance, setPerformance] = useState();
  const [error, setError] = useState();
  const [sort, setSort] = useState({ by: 'totalConversionValue', direction: 'desc' });

  useEffect(() => {
    let cancelled = false;
    setPerformance(undefined);
    setError(undefined);

    segmentPerformanceDebounced(
      fetch,
      accountId,
      domainId,
      dateToStartOfDay(startDate, 'local'),
      dateToEndOfDay(endDate, 'local')
    )
      .then((data) => {
        if (!cancelled) setPerformance(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, startDate, endDate]);

  const segments = performance?.segments;
  const channels = useMemo(() => activeChannelKeys(segments), [segments]);
  const rows = useMemo(() => sortSegmentRows(segments, sort.by, sort.direction), [segments, sort]);

  const onSort = (sortKey) =>
    setSort((current) =>
      current.by === sortKey
        ? { by: sortKey, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { by: sortKey, direction: defaultDirectionFor(sortKey) }
    );

  const datePicker = (
    <DateRangePicker
      preSetRanges
      startDate={startDate}
      endDate={endDate}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      maxDate={todayISODate()}
    />
  );

  if (error) {
    return (
      <Stack gap="md" mt="md">
        {datePicker}
        <ErrorMessage message={getErrorMessage(error)} />
      </Stack>
    );
  }

  if (performance === undefined) {
    return (
      <Stack gap="md" mt="md">
        {datePicker}
        <Spinner />
      </Stack>
    );
  }

  // Only starred segments are reported, and nothing starred is the default state
  // of a domain - so this is an explanation, not an error.
  if (rows.length === 0) {
    return (
      <Stack gap="md" mt="md">
        {datePicker}
        <Alert icon={<IconStarOff size={16} />} color="gray" variant="light" title="No starred segments yet">
          This tab reports the segments you star. Open{' '}
          <Anchor href={`#/accounts/${accountId}/domains/${domainId}/segments`}>Segments</Anchor> and use the star on a
          segment card to start tracking it here — up to 25 segments per domain.
        </Alert>
      </Stack>
    );
  }

  const sizeHistoryNotice = describeSizeHistory(rows, performance.earliestSnapshotDate);

  return (
    <Stack gap="md" mt="md">
      {datePicker}

      {sizeHistoryNotice && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          {sizeHistoryNotice}
        </Alert>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <SortableTh sortKey="name" label="Segment" sort={sort} onSort={onSort} />
            <SortableTh sortKey="members" label="Members" sort={sort} onSort={onSort} />
            <SortableTh sortKey="sizeChange" label="Size change" sort={sort} onSort={onSort} />
            <SortableTh sortKey="totalConversions" label="Conversions" sort={sort} onSort={onSort} />
            <SortableTh sortKey="totalConversionValue" label="Conversion value" sort={sort} onSort={onSort} />
            {channels.map((channel) => (
              <SortableTh
                key={channel}
                sortKey={channelSortKey(channel)}
                label={channelLabel(channel)}
                sort={sort}
                onSort={onSort}
              />
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((segment) => (
            <Table.Tr key={segment.segmentId}>
              <Table.Td>
                <Anchor href={`#/accounts/${accountId}/domains/${domainId}/segments/${segment.segmentId}`} size="sm">
                  {segment.name}
                </Anchor>
              </Table.Td>
              <Table.Td>{formatCount(segment.members)}</Table.Td>
              <Table.Td>
                <SizeChangeCell sizeChange={segment.sizeChange} sizeAtStart={segment.sizeAtStart} />
              </Table.Td>
              <Table.Td>
                {segment.totalConversions == null ? <NotTrackedCell /> : formatCount(segment.totalConversions)}
              </Table.Td>
              <Table.Td>
                {segment.totalConversionValue == null ? (
                  <NotTrackedCell />
                ) : (
                  formatConversionValue(segment.totalConversionValue, currency)
                )}
              </Table.Td>
              {channels.map((channel) => {
                const entry = segment.byChannel?.[channel];
                return (
                  <Table.Td key={channel}>
                    {entry ? (
                      <Stack gap={0}>
                        <Text size="sm">{formatConversionValue(entry.value, currency)}</Text>
                        <Text size="xs" c="dimmed">
                          {formatCount(entry.conversions)} conv.
                        </Text>
                      </Stack>
                    ) : (
                      EM_DASH
                    )}
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default AudiencePerformanceTab;
